import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, UserLogin, UserRegister } from '../types/user';
import { Company } from '../types/company';
import { api } from '../services/api';
import { tokenService } from '../services/tokenService';
import { useNavigate } from 'react-router-dom';
import { logger } from '../utils/logger';

interface AuthContextType {
    isAuthenticated: boolean;
    loading: boolean;
    login: (data: UserLogin, onSuccess?: () => void) => Promise<void>;
    googleLogin: (userData: any, onSuccess?: () => void) => Promise<void>;
    register: (data: UserRegister) => Promise<User | null>;
    createCompany: (companyData: any) => Promise<Company | null>;
    logout: () => void;
    logoutAndRedirect: () => void;
    user: User | null;
    refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
    isAuthenticated: false,
    loading: true,
    login: async () => {},
    googleLogin: async () => {},
    register: async () => null,
    createCompany: async () => null,
    logout: () => {},
    logoutAndRedirect: () => {},
    user: null,
    refreshUser: async () => {},
});


// After a forced re-login (expired session), return the user to where they were.
const consumePostLoginRedirect = (): string => {
    try {
        const target = sessionStorage.getItem('gt_post_login_redirect');
        sessionStorage.removeItem('gt_post_login_redirect');
        if (target && target.startsWith('/') && !target.startsWith('//') && !target.startsWith('/login')) {
            return target;
        }
    } catch {}
    return '/dashboard';
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    // Utility function for forced logout on authentication failure
    const logoutAndRedirect = () => {
        logger.log('[AuthContext] Forced logout due to authentication failure');
        tokenService.removeToken();
        setUser(null);
        setIsAuthenticated(false);
        navigate('/login');
    };

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const token = tokenService.getToken();
                if (token) {
                    try {
                        // Initialize token service
                        tokenService.initialize();
                        
                        const response = await api.get('/api/v1/auth/me');
                        setUser(response.data);
                        setIsAuthenticated(true);
                    } catch (authErr: any) {
                        // If token is expired (401) or invalid (422), try to refresh
                        if (authErr.response?.status === 401 || authErr.response?.status === 422) {
                            logger.log("Token expired or invalid, attempting refresh");
                            try {
                                await tokenService.refreshToken();
                                // Retry getting user data
                                const userResponse = await api.get('/api/v1/auth/me');
                                setUser(userResponse.data);
                                setIsAuthenticated(true);
                            } catch (refreshErr) {
                                logger.log("Token refresh failed, clearing authentication");
                                tokenService.removeToken();
                                setUser(null);
                                setIsAuthenticated(false);
                                navigate('/login');
                            }
                        } else {
                            // Network/server errors (e.g., 5xx, timeouts) should not log the user out.
                            // Mark as temporarily unavailable and retry shortly.
                            const status = authErr?.response?.status;
                            logger.warn('[AuthContext] /me failed with status:', status);
                            setIsAuthenticated(Boolean(tokenService.getToken()));
                            // schedule a retry without clearing token
                            setTimeout(checkAuth, 5000);
                            return;
                        }
                    }
                }
            } catch (err) {
                // Only hard-logout on explicit auth failures; otherwise keep session and retry later
                const status = (err as any)?.response?.status;
                logger.error("Auth check failed:", err);
                if (status === 401 || status === 422) {
                    tokenService.removeToken();
                    setUser(null);
                    setIsAuthenticated(false);
                } else {
                    // transient error - keep token and state, retry later
                    setIsAuthenticated(Boolean(tokenService.getToken()));
                    setTimeout(checkAuth, 5000);
                    return;
                }
            } finally {
                setLoading(false);
            }
        };

        checkAuth();
    }, [navigate]);

    const login = async (data: UserLogin, onSuccess?: () => void) => {
        try {
            logger.log('Starting login process...');
            
            // Convert to OAuth2 password flow format
            const formData = new URLSearchParams();
            formData.append('username', data.email);
            formData.append('password', data.password);

            logger.log('Sending login request...');
            let response;
            try {
                response = await api.post('/api/v1/auth/login', formData, {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'X-API-Key': process.env.REACT_APP_API_KEY || 'test-key-123'
                    },
                });
            } catch (err) {
                // If the API is mocked, it might not handle URLSearchParams correctly
                if (err instanceof TypeError && err.message.includes('formData.get is not a function')) {
                    logger.log('Retrying with plain object for mock API');
                    response = await api.post('/api/v1/auth/login', {
                        username: data.email,
                        password: data.password
                    }, {
                        headers: {
                            'Content-Type': 'application/json',
                            'X-API-Key': process.env.REACT_APP_API_KEY || 'test-key-123'
                        },
                    });
                } else {
                    throw err;
                }
            }
            
            logger.log('Login response received:', response.data);
            
            // Make sure we have a valid token structure
            if (!response.data || !response.data.access_token) {
                throw new Error('Invalid response from server: no access token');
            }
            
            // Store the access token using token service
            const { access_token } = response.data;
            tokenService.setToken(access_token);
            
            // Initialize token service
            tokenService.initialize();
            
            logger.log('Fetching user data...');
            // Fetch user data with the token
            try {
                const userResponse = await api.get('/api/v1/auth/me', {
                    headers: {
                        'Authorization': `Bearer ${access_token}`,
                        'X-API-Key': process.env.REACT_APP_API_KEY || 'test-key-123'
                    }
                });
                logger.log('User data received:', userResponse.data);
                
                // When setting user, always include is_company_admin
                setUser({ ...userResponse.data, is_company_admin: userResponse.data.is_company_admin, is_app_admin: Boolean(userResponse.data.is_app_admin) });
                setIsAuthenticated(true);
                if (onSuccess) onSuccess();
                logger.log('Authentication state updated, navigating to dashboard...');
                navigate(consumePostLoginRedirect());
            } catch (userError) {
                logger.error('Error fetching user data:', userError);
                throw new Error('Authentication succeeded but user data could not be retrieved');
            }
        } catch (err: any) {
            logger.error('Login error:', err);
            
            // Clear any existing tokens
            tokenService.removeToken();
            
            // Format a more useful error message
            let errorMsg = 'An error occurred during login. Please try again.';
            
            if (err.response) {
                if (err.response.status === 401) {
                    errorMsg = 'Invalid email or password. Please try again.';
                } else if (err.response.data?.detail) {
                    errorMsg = err.response.data.detail;
                }
            } else if (err.message) {
                errorMsg = err.message;
            }
            
            throw new Error(errorMsg);
        }
    };

    const googleLogin = async (userData: any, onSuccess?: () => void) => {
        try {
            logger.log('Starting Google login process...');
            
            // Store the access token using token service
            const { access_token } = userData;
            tokenService.setToken(access_token);
            
            // Initialize token service
            tokenService.initialize();
            
            logger.log('Fetching user data for Google login...');
            // Fetch user data with the token
            try {
                const userResponse = await api.get('/api/v1/auth/me', {
                    headers: {
                        'Authorization': `Bearer ${access_token}`,
                        'X-API-Key': process.env.REACT_APP_API_KEY || 'test-key-123'
                    }
                });
                logger.log('Google user data received:', userResponse.data);
                
                // Set user data and authentication state
                setUser({ ...userResponse.data, is_company_admin: userResponse.data.is_company_admin, is_app_admin: Boolean(userResponse.data.is_app_admin) });
                setIsAuthenticated(true);
                logger.log('Google authentication state updated, navigating to dashboard...');
                
                // Navigate immediately without setTimeout to avoid state conflicts
                if (onSuccess) onSuccess();
                navigate(consumePostLoginRedirect());
            } catch (userError) {
                logger.error('Error fetching user data for Google login:', userError);
                throw new Error('Google authentication succeeded but user data could not be retrieved');
            }
        } catch (err: any) {
            logger.error('Google login error:', err);
            
            // Clear any existing tokens
            tokenService.removeToken();
            
            // Format a more useful error message
            let errorMsg = 'An error occurred during Google sign-in. Please try again.';
            
            if (err.response) {
                if (err.response.status === 401) {
                    errorMsg = 'Google sign-in failed. Please try again.';
                } else if (err.response.data?.detail) {
                    errorMsg = err.response.data.detail;
                }
            } else if (err.message) {
                errorMsg = err.message;
            }
            
            throw new Error(errorMsg);
        }
    };

    // Update createCompany to return the updated user object (or null on failure)
    const createCompany = async (companyData: any): Promise<Company | null> => {
        try {
            logger.log('[AuthContext] Attempting to create company with data:', companyData);
            
            // CRITICAL FIX: Ensure we have a valid token before making the request
            const token = localStorage.getItem('token');
            if (!token) {
                logger.error('[AuthContext] No authentication token found');
                throw new Error('No authentication token found. Please log in again.');
            }
            
            // Verify token is still valid
            try {
                await api.get('/api/v1/auth/me');
            } catch (authError: any) {
                if (authError.response?.status === 401) {
                    logger.error('[AuthContext] Token is invalid, redirecting to login');
                    logout();
                    throw new Error('Your session has expired. Please log in again.');
                }
            }
            
            const response = await api.post('/api/v1/companies/', {
                name: companyData.name,
                tax_id: companyData.tax_id,
                address_line1: companyData.address_line1,
                city: companyData.city,
                state: companyData.state,
                zip_code: companyData.zip_code,
                country: companyData.country || 'US',
                is_head_office: companyData.is_head_office !== undefined ? companyData.is_head_office : true
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'X-API-Key': process.env.REACT_APP_API_KEY || 'test-key-123'
                }
            });
            
            logger.log('[AuthContext] createCompany response:', response.data);
            const createdCompany: Company | null = response.data || null;

            if (createdCompany && createdCompany.id) {
                // Fetch updated user
                const userResponse = await api.get('/api/v1/auth/me');
                logger.log('[AuthContext] Fetched user after company creation:', userResponse.data);
                setUser(userResponse.data);
                setIsAuthenticated(true);
                logger.log('[AuthContext] setUser called after company creation:', userResponse.data);
                return createdCompany;
            }
            logger.warn('[AuthContext] createCompany: No company id in response');
            return null;
        } catch (error: any) {
            logger.error('[AuthContext] Failed to create company:', error);
            
            // Handle authentication errors
            if (error.response?.status === 401 || error.response?.status === 403) {
                logger.error('[AuthContext] Authentication error, logging out user');
                logout();
                throw new Error('Your session has expired. Please log in again.');
            }
            
            // Re-throw the error with proper message
            throw error;
        }
    };

    const register = async (data: UserRegister) => {
        try {
            // Remove confirmPassword before sending to API
            const { confirmPassword, ...registrationData } = data;
            logger.log('[AuthContext] Sending registration request with:', registrationData);
            const response = await api.post('/api/v1/auth/register', registrationData);
            logger.log('[AuthContext] Registration response received:', response.data);
            const { access_token } = response.data;
            // Store the access token using token service
            tokenService.setToken(access_token);
            tokenService.initialize();
            // Fetch the full user object after registration
            const userResponse = await api.get('/api/v1/auth/me');
            logger.log('[AuthContext] User fetched after registration:', userResponse.data);
            setUser(userResponse.data);
            setIsAuthenticated(true);
            logger.log('[AuthContext] setUser called after registration:', userResponse.data);
            return userResponse.data;
        } catch (error: any) {
            logger.error('[AuthContext] Registration error:', error);
            
            // Default error message
            let userMessage = 'An unexpected error occurred. Please try again later.';
            const errorDetail = error?.response?.data?.detail || '';
            const status = error?.response?.status;
            
            // Map specific error messages from the backend
            if (status === 400) {
                if (errorDetail.includes('email already exists') || errorDetail.includes('already registered')) {
                    userMessage = 'An account with this email already exists. Please use a different email or sign in.';
                } else if (errorDetail.includes('valid email')) {
                    userMessage = 'Please enter a valid email address.';
                } else if (errorDetail.includes('password')) {
                    userMessage = 'Password must be at least 8 characters long.';
                } else if (errorDetail.includes('full name')) {
                    userMessage = 'Please enter your full name.';
                } else if (errorDetail.includes('company')) {
                    userMessage = 'There was an issue with the company information. Please check and try again.';
                } else {
                    userMessage = errorDetail || 'Please check your information and try again.';
                }
            } else if (status === 409) {
                userMessage = 'An account with this email already exists. Please sign in instead.';
            } else if (status === 503) {
                userMessage = 'Our service is temporarily unavailable. Please try again in a few minutes.';
            } else if (!error.response) {
                userMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
            }
            
            // Create a new error with the user-friendly message
            const userError = new Error(userMessage);
            userError.name = error.name || 'RegistrationError';
            
            throw userError;
        }
    };

    const logout = () => {
        logger.log('[AuthContext] User logout');
        tokenService.removeToken();
        setUser(null);
        setIsAuthenticated(false);
        navigate('/login');
    };

    // Add refreshUser function to allow manual rehydration of user context
    const refreshUser = async () => {
        setLoading(true);
        try {
            const response = await api.get('/api/v1/auth/me');
            setUser(response.data);
            setIsAuthenticated(true);
        } catch (err) {
            // Only an auth failure ends the session; a 5xx/timeout must not bounce the user to /login.
            const status = (err as any)?.response?.status;
            if (status === 401 || status === 422 || !tokenService.getToken()) {
                setUser(null);
                setIsAuthenticated(false);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthContext.Provider value={{
            isAuthenticated,
            loading,
            user,
            login,
            googleLogin,
            register,
            createCompany, // now accepts a name param
            logout,
            logoutAndRedirect, // <-- add this
            refreshUser, // <-- add this
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext); 
